import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { IconButton, FullscreenIcon, FullscreenExitIcon } from '@/ui';
import type { TypeHTMLElement } from '@/core/types/global-types';
import { getSxClasses } from '@/core/components/nav-bar/nav-bar-style';
import { useAppStoreActions, useAppFullscreenActive } from '@/core/stores/store-interface-and-intial-values/app-state';
import { useGeoViewMapId } from '@/core/stores/geoview-store';
import { logger } from '@/core/utils/logger';

/**
 * Creates a toggle button to toggle between fullscreen.
 *
 * @returns The fullscreen toggle button
 */
export default function Fullscreen(): JSX.Element {
  // Log
  logger.logTraceRender('components/nav-bar/buttons/fullscreen');

  const mapId = useGeoViewMapId();

  const { t } = useTranslation<string>();
  const theme = useTheme();
  const sxClasses = getSxClasses(theme);

  // get the values from store
  const isFullScreen = useAppFullscreenActive();
  const { setFullScreenActive } = useAppStoreActions();

  /**
   * Toggles between fullscreen and window mode.
   */
  function setFullscreen(): void {
    const element = document.getElementById(`shell-${mapId}`);
    if (element) {
      setFullScreenActive(!isFullScreen, element as TypeHTMLElement);

      // TODO: deprecated, do we still need this? January 30th 2026
      // setFooterBarIsOpen(false);
    }
  }

  /**
   * Registers fullscreen change listeners to detect ESC key exit.
   */
  useEffect(() => {
    // Log
    logger.logTraceUseEffect('FULLSCREEN - mount');

    /**
     * Exits fullscreen when the browser fullscreen state changes.
     */
    function handleExit(): void {
      if (!document.fullscreenElement) {
        setFullScreenActive(false);
      }
    }
    document.addEventListener('fullscreenchange', handleExit);
    document.addEventListener('webkitfullscreenchange', handleExit);
    document.addEventListener('mozfullscreenchange', handleExit);
    document.addEventListener('MSFullscreenChange', handleExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleExit);
      document.removeEventListener('webkitfullscreenchange', handleExit);
      document.removeEventListener('mozfullscreenchange', handleExit);
      document.removeEventListener('MSFullscreenChange', handleExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <IconButton
      id="fullscreen"
      aria-label={t('mapnav.fullscreen')}
      tooltipPlacement="left"
      onClick={() => setFullscreen()}
      sx={sxClasses.navButton}
    >
      {!isFullScreen ? <FullscreenIcon /> : <FullscreenExitIcon />}
    </IconButton>
  );
}
